/**
 * Toast Notification Component
 *
 * Renders toast notifications in the bottom-right corner of the viewport.
 * Supports four variants: default (info), success, error, and warning.
 * Styled to match the application's shadcn/ui theme and CSS variable system.
 *
 * Usage: import { useToast } from '@/components/ui/use-toast'
 *        const { toast } = useToast()
 *        toast({ title: 'Saved', description: 'Changes saved.', variant: 'success' })
 */

"use client";

import * as React from "react";

/* ─── Types ─── */

export type ToastVariant = "default" | "success" | "error" | "warning";

export interface ToastData {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  /** Auto-dismiss duration in ms. Default 5000. Set 0 to persist until dismissed. */
  duration?: number;
}

/* ─── Variant Styles ─── */

const variantStyles: Record<ToastVariant, string> = {
  default:
    "bg-card text-card-foreground border-border",
  success:
    "bg-green-50 text-green-900 border-green-200 dark:bg-green-950 dark:text-green-100 dark:border-green-800",
  error:
    "bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-800",
  warning:
    "bg-yellow-50 text-yellow-900 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800",
};

const iconMap: Record<ToastVariant, string> = {
  default: "ℹ",
  success: "✓",
  error: "✕",
  warning: "⚠",
};

const iconColorMap: Record<ToastVariant, string> = {
  default: "text-primary",
  success: "text-green-600 dark:text-green-400",
  error: "text-red-600 dark:text-red-400",
  warning: "text-yellow-600 dark:text-yellow-400",
};

/* ─── Single Toast ─── */

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastData;
  onDismiss: (id: string) => void;
}) {
  const variant = toast.variant ?? "default";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        pointer-events-auto w-full max-w-sm rounded-lg border p-4 shadow-lg
        animate-in slide-in-from-right-full fade-in duration-300
        ${variantStyles[variant]}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${iconColorMap[variant]}`}
        >
          {iconMap[variant]}
        </span>

        {/* Content */}
        <div className="flex-1 space-y-1">
          {toast.title && (
            <p className="text-sm font-semibold leading-none">{toast.title}</p>
          )}
          {toast.description && (
            <p className="text-sm opacity-80 leading-snug">
              {toast.description}
            </p>
          )}
        </div>

        {/* Dismiss */}
        <button
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 rounded-md p-1 opacity-60 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Dismiss notification"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ─── Toast Container (rendered by ToastProvider) ─── */

export function ToastContainer({
  toasts,
  dismiss,
}: {
  toasts: ToastData[];
  dismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}
